/**
 * Client-side encryption for cloud-stored user data.
 *
 * Mirrors the Dart CloudEncryptionService exactly:
 *   - Key derivation: SHA-256(Firebase UID + salt) → 32-byte AES key
 *   - Algorithm: AES-256-CBC with random 16-byte IV
 *   - Wire format: base64(IV_16_bytes + ciphertext)
 *
 * Uses the Web Crypto API (built into all modern browsers).
 */

import { getCurrentUser } from './auth'

const KEY_SALT = 'PiPar_Cloud_E2E_2025_v1'

let cachedKey: CryptoKey | null = null
let cachedUid: string | null = null

/** Derive or return cached AES-256-CBC key for the current user. */
async function getKey(): Promise<CryptoKey | null> {
  const user = getCurrentUser()
  if (!user) return null
  const uid = user.uid

  if (cachedKey && cachedUid === uid) return cachedKey

  const encoder = new TextEncoder()
  const material = encoder.encode(`${uid}:${KEY_SALT}`)

  // SHA-256 hash → 32-byte raw key
  const hashBuffer = await crypto.subtle.digest('SHA-256', material)

  cachedKey = await crypto.subtle.importKey(
    'raw',
    hashBuffer,
    { name: 'AES-CBC', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
  cachedUid = uid
  return cachedKey
}

/** Clear cached key (call on sign-out). */
export function clearEncryptionCache(): void {
  cachedKey = null
  cachedUid = null
}

// ── String encryption ──────────────────────────────────────────

/** Encrypt a plaintext string → base64(IV + ciphertext). */
export async function encryptString(plaintext: string): Promise<string | null> {
  const key = await getKey()
  if (!key) return null

  const encoder = new TextEncoder()
  const data = encoder.encode(plaintext)

  const iv = crypto.getRandomValues(new Uint8Array(16))
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, key, data)

  const combined = new Uint8Array(16 + ciphertext.byteLength)
  combined.set(iv, 0)
  combined.set(new Uint8Array(ciphertext), 16)

  return btoa(String.fromCharCode(...combined))
}

/** Decrypt base64(IV + ciphertext) → plaintext string. */
export async function decryptString(encryptedBase64: string): Promise<string | null> {
  const key = await getKey()
  if (!key) return null

  try {
    const combined = Uint8Array.from(atob(encryptedBase64), (c) => c.charCodeAt(0))
    if (combined.length < 17) return null

    const iv = combined.slice(0, 16)
    const ciphertext = combined.slice(16)

    const plainBuffer = await crypto.subtle.decrypt({ name: 'AES-CBC', iv }, key, ciphertext)
    return new TextDecoder().decode(plainBuffer)
  } catch {
    return null
  }
}

// ── JSON encryption ────────────────────────────────────────────

/** Decrypt a cloud data item. If it has `_encrypted`, decrypt it; otherwise return as-is. */
export async function decryptJson(data: Record<string, unknown>): Promise<Record<string, unknown> | null> {
  const encrypted = data._encrypted as string | undefined
  if (!encrypted) return data // Not encrypted, return as-is

  try {
    const decrypted = await decryptString(encrypted)
    if (!decrypted) return null
    return JSON.parse(decrypted) as Record<string, unknown>
  } catch {
    return null
  }
}

/** Check if data is encrypted. */
export function isEncrypted(data: Record<string, unknown>): boolean {
  return '_encrypted' in data && Object.keys(data).filter((k) => !k.startsWith('_cloud')).length <= 3
}

// ── Bytes encryption (for blob downloads) ──────────────────────

/** Decrypt encrypted blob bytes → plaintext bytes. */
export async function decryptBytes(encryptedBytes: Uint8Array): Promise<Uint8Array | null> {
  const key = await getKey()
  if (!key) return null

  try {
    if (encryptedBytes.length < 17) return null

    const iv = encryptedBytes.slice(0, 16)
    const ciphertext = encryptedBytes.slice(16)

    const plainBuffer = await crypto.subtle.decrypt({ name: 'AES-CBC', iv }, key, ciphertext)
    return new Uint8Array(plainBuffer)
  } catch {
    return null
  }
}
